import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { MalayalamTtsSpeakOptions } from '../models/malayalam-tts.model';
import { LoggerService } from './logger.service';
import { MalayalamTtsRepository } from './malayalam-tts.repository';

const SPEECH_CACHE_MAX = 12;
const PROGRESS_INTERVAL_MS = 75;
const LEGACY_PIPER_DB = 'logibids-piper-tts';
const SERVER_UNAVAILABLE_KEY = 'logibids-ml-tts-unavailable';

type ServerTtsAvailability = 'unknown' | 'available' | 'unavailable';

/**
 * Server-side Malayalam TTS (female Anjali via backend proxy).
 * English/Arabic continue to use SpeechSynthesis in ai-cab-booking.
 */
@Injectable({ providedIn: 'root' })
export class MalayalamTtsService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly logger = inject(LoggerService);
  private readonly repository = inject(MalayalamTtsRepository);

  private readonly serverAvailability = signal<ServerTtsAvailability>('unknown');
  private readonly warmed = signal(false);
  private warmupPromise: Promise<void> | null = null;
  private warmupAbort: AbortController | null = null;

  private volumeMuted = false;
  private audio: HTMLAudioElement | null = null;
  private objectUrl: string | null = null;
  private progressInterval: ReturnType<typeof setInterval> | null = null;
  private speakGeneration = 0;
  private activePlayFinish: (() => void) | null = null;
  private synthesizeAbort: AbortController | null = null;

  private readonly speechCache = new Map<string, Blob>();
  private readonly prefetchPromises = new Map<string, Promise<void>>();

  /** Returns true when Malayalam server TTS should be used instead of SpeechSynthesis. */
  isMalayalamLanguage(appLang: string): boolean {
    return appLang.trim().toLowerCase() === 'ml';
  }

  /** True when Malayalam is active and the backend TTS API is enabled and reachable. */
  shouldUseServerTts(appLang: string): boolean {
    return (
      this.isMalayalamLanguage(appLang) &&
      this.isServerTtsConfigured() &&
      this.serverAvailability() !== 'unavailable'
    );
  }

  isSupported(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  isReady(): boolean {
    return this.warmed();
  }

  /**
   * Warms the backend TTS worker when the user selects Malayalam.
   * No-ops when the API is disabled or previously returned 404.
   */
  warmup(): Promise<void> {
    if (!this.isSupported() || !this.canContactServer()) {
      return Promise.resolve();
    }

    if (this.warmed() || this.serverAvailability() === 'available') {
      return Promise.resolve();
    }

    if (this.warmupPromise) {
      return this.warmupPromise;
    }

    this.warmupAbort?.abort();
    this.warmupAbort = new AbortController();

    this.warmupPromise = firstValueFrom(this.repository.warmup(this.warmupAbort.signal))
      .then(() => {
        this.serverAvailability.set('available');
        this.warmed.set(true);
      })
      .catch(error => {
        if (this.isAbortError(error)) {
          return;
        }

        if (this.isServerUnavailableError(error)) {
          this.markServerUnavailable();
          return;
        }

        this.logger.debug('Malayalam TTS warmup failed', error);
      })
      .finally(() => {
        this.warmupPromise = null;
        this.warmupAbort = null;
      });

    return this.warmupPromise;
  }

  /** Alias used by ai-cab-booking bootstrap — keeps chat open snappy after header warmup. */
  preload(): Promise<void> {
    return this.warmup();
  }

  /**
   * Pre-synthesizes speech while the welcome API loads (ml only).
   * Stores audio blobs in memory only — cleared on dispose().
   */
  prepareSpeech(text: string): Promise<void> {
    if (!this.isSupported() || !this.canContactServer()) {
      return Promise.resolve();
    }

    const key = text.trim();
    if (!key || this.speechCache.has(key)) {
      return Promise.resolve();
    }

    const inFlight = this.prefetchPromises.get(key);
    if (inFlight) {
      return inFlight;
    }

    const promise = this.warmup()
      .then(async () => {
        if (!this.canContactServer() || this.speechCache.has(key)) {
          return;
        }

        const blob = await this.fetchAudioBlob(key, { prefetch: true });
        this.speechCache.set(key, blob);
        this.trimSpeechCache();
      })
      .catch(error => {
        if (this.isAbortError(error)) {
          return;
        }

        if (this.isServerUnavailableError(error)) {
          this.markServerUnavailable();
          return;
        }

        this.logger.debug('Malayalam TTS prefetch failed', error);
      })
      .finally(() => {
        this.prefetchPromises.delete(key);
      });

    this.prefetchPromises.set(key, promise);
    return promise;
  }

  /**
   * Clears in-memory audio cache and stops playback when leaving Malayalam.
   * Also removes legacy Piper IndexedDB data from older builds.
   */
  dispose(): void {
    this.warmupAbort?.abort();
    this.warmupAbort = null;
    this.warmupPromise = null;
    this.warmed.set(false);
    this.serverAvailability.set('unknown');
    this.clearServerUnavailableFlag();

    this.synthesizeAbort?.abort();
    this.synthesizeAbort = null;

    this.speechCache.clear();
    this.prefetchPromises.clear();

    this.speakGeneration++;
    this.stopPlaybackInternal(false);

    this.clearLegacyPiperCache();
  }

  isPlaying(): boolean {
    return !!this.audio && !this.audio.paused && !this.audio.ended;
  }

  isPausedByMute(): boolean {
    return this.volumeMuted;
  }

  /** Mutes/unmutes playback without pausing — text sync and flow continue. */
  setVolumeMuted(muted: boolean): void {
    this.volumeMuted = muted;
    if (this.audio) {
      this.audio.muted = muted;
    }
  }

  isVolumeMuted(): boolean {
    return this.volumeMuted;
  }

  /**
   * Fetches and plays Malayalam speech from the backend.
   * Resolves when playback completes or is stopped.
   */
  async speak(text: string, options: MalayalamTtsSpeakOptions = {}): Promise<void> {
    if (!this.isSupported() || !text.trim()) {
      return;
    }

    if (!this.canContactServer()) {
      throw new Error('Malayalam server TTS is not available');
    }

    const generation = ++this.speakGeneration;
    this.stopPlaybackInternal(false);

    try {
      options.onPreparing?.();

      await this.warmup();
      if (generation !== this.speakGeneration || !this.canContactServer()) {
        if (!this.canContactServer()) {
          throw new Error('Malayalam server TTS is not available');
        }
        return;
      }

      const key = text.trim();
      let audioBlob = this.speechCache.get(key);
      if (!audioBlob) {
        audioBlob = await this.fetchAudioBlob(key);
        if (generation !== this.speakGeneration) {
          return;
        }
        this.speechCache.set(key, audioBlob);
        this.trimSpeechCache();
      }

      await this.playBlob(audioBlob, generation, options);
    } catch (error) {
      if (this.isAbortError(error) || generation !== this.speakGeneration) {
        return;
      }

      if (this.isServerUnavailableError(error)) {
        this.markServerUnavailable();
        throw new Error('Malayalam server TTS is not available');
      }

      this.logger.error('Malayalam TTS failed', error);
      throw error;
    }
  }

  stop(): void {
    this.speakGeneration++;
    this.synthesizeAbort?.abort();
    this.synthesizeAbort = null;
    this.stopPlaybackInternal(true);
  }

  pause(): void {
    this.setVolumeMuted(true);
  }

  resume(): void {
    this.setVolumeMuted(false);
  }

  private isServerTtsConfigured(): boolean {
    return environment.malayalamTtsServerEnabled === true;
  }

  private canContactServer(): boolean {
    if (!this.isServerTtsConfigured()) {
      return false;
    }

    if (this.serverAvailability() === 'unavailable') {
      return false;
    }

    return !this.isServerMarkedUnavailable();
  }

  private markServerUnavailable(): void {
    this.serverAvailability.set('unavailable');
    this.warmed.set(false);

    if (this.isSupported()) {
      try {
        sessionStorage.setItem(SERVER_UNAVAILABLE_KEY, '1');
      } catch {
        // Ignore quota / privacy mode errors.
      }
    }
  }

  private clearServerUnavailableFlag(): void {
    if (!this.isSupported()) {
      return;
    }

    try {
      sessionStorage.removeItem(SERVER_UNAVAILABLE_KEY);
    } catch {
      // Ignore storage errors.
    }
  }

  private isServerMarkedUnavailable(): boolean {
    if (!this.isSupported()) {
      return false;
    }

    try {
      return sessionStorage.getItem(SERVER_UNAVAILABLE_KEY) === '1';
    } catch {
      return false;
    }
  }

  private isServerUnavailableError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    return error.status === 404 || error.status === 405 || error.status === 501;
  }

  private async fetchAudioBlob(text: string, options: { prefetch?: boolean } = {}): Promise<Blob> {
    this.synthesizeAbort?.abort();
    this.synthesizeAbort = new AbortController();

    let blob: Blob;

    try {
      blob = await firstValueFrom(
        this.repository.synthesize(text, {
          prefetch: options.prefetch,
          signal: this.synthesizeAbort.signal,
        }),
      );
    } catch (error) {
      if (this.isServerUnavailableError(error)) {
        this.markServerUnavailable();
      }
      throw error;
    }

    if (blob.type.includes('json') || blob.type.includes('text')) {
      const details = await blob.text();
      throw new Error(details || 'Malayalam TTS synthesis failed');
    }

    if (!blob || blob.size === 0) {
      throw new Error('Malayalam TTS returned empty audio');
    }

    return blob;
  }

  private trimSpeechCache(): void {
    while (this.speechCache.size > SPEECH_CACHE_MAX) {
      const oldest = this.speechCache.keys().next().value;
      if (oldest === undefined) {
        break;
      }
      this.speechCache.delete(oldest);
    }
  }

  private playBlob(
    blob: Blob,
    generation: number,
    options: MalayalamTtsSpeakOptions,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.revokeObjectUrl();
      this.objectUrl = URL.createObjectURL(blob);
      const audio = new Audio(this.objectUrl);
      this.audio = audio;
      audio.muted = this.volumeMuted || !!options.muted;

      const finish = (): void => {
        if (this.activePlayFinish === finishPlay) {
          this.activePlayFinish = null;
        }
        this.clearProgressInterval();
        if (generation === this.speakGeneration) {
          this.cleanupAudio();
        }
        resolve();
      };

      const finishPlay = finish;
      this.activePlayFinish = finishPlay;

      audio.onloadedmetadata = () => {
        if (generation !== this.speakGeneration) {
          finish();
          return;
        }
        options.onStart?.();
        this.startProgressTracking(audio, generation, options.onProgress);
      };

      audio.onended = () => {
        options.onProgress?.(1);
        finish();
      };

      audio.onerror = () => {
        if (this.activePlayFinish === finishPlay) {
          this.activePlayFinish = null;
        }
        this.clearProgressInterval();
        if (generation === this.speakGeneration) {
          this.cleanupAudio();
        }
        reject(new Error('Malayalam TTS audio playback failed'));
      };

      void audio.play().catch(err => {
        if (this.activePlayFinish === finishPlay) {
          this.activePlayFinish = null;
        }
        this.logger.warn('Malayalam TTS play() failed', err);
        reject(err);
      });
    });
  }

  private startProgressTracking(
    audio: HTMLAudioElement,
    generation: number,
    onProgress?: (ratio: number) => void,
  ): void {
    if (!onProgress) {
      return;
    }

    this.clearProgressInterval();
    this.progressInterval = setInterval(() => {
      if (generation !== this.speakGeneration || !this.audio) {
        this.clearProgressInterval();
        return;
      }

      const duration = audio.duration;
      if (!Number.isFinite(duration) || duration <= 0) {
        return;
      }

      onProgress(Math.min(1, audio.currentTime / duration));
    }, PROGRESS_INTERVAL_MS);
  }

  private stopPlaybackInternal(_resetMuteFlag: boolean): void {
    this.clearProgressInterval();
    this.haltActiveAudio();
    this.cleanupAudio();
    this.activePlayFinish?.();
    this.activePlayFinish = null;
  }

  /** Fully stops HTMLAudioElement playback (required on Safari / mobile). */
  private haltActiveAudio(): void {
    if (!this.audio) {
      return;
    }

    const audio = this.audio;
    audio.onended = null;
    audio.onerror = null;

    try {
      audio.pause();
    } catch {
      // Ignore pause errors during teardown.
    }

    try {
      audio.currentTime = 0;
    } catch {
      // Ignore seek errors before metadata is ready.
    }

    audio.removeAttribute('src');

    try {
      audio.load();
    } catch {
      // Ignore load errors during teardown.
    }
  }

  private cleanupAudio(): void {
    this.revokeObjectUrl();
    this.audio = null;
  }

  private revokeObjectUrl(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }

  private clearProgressInterval(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  private clearLegacyPiperCache(): void {
    if (typeof indexedDB === 'undefined') {
      return;
    }

    try {
      indexedDB.deleteDatabase(LEGACY_PIPER_DB);
    } catch (error) {
      this.logger.warn('Legacy Piper IndexedDB cleanup failed', error);
    }
  }

  private isAbortError(error: unknown): boolean {
    return error instanceof DOMException && error.name === 'AbortError';
  }
}
